import React, { useMemo } from "react";
import { MenuItem, Stack, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTypes } from "../hooks/useTypes";
import {
  asNumOrNull,
  emptyLeadTypeValue,
  titleOfType,
} from "../utils/leadTypeCascade";

/**
 * Selects em cascata Tipo → Subtipo 1–3 (mesmo padrão do cadastro de canais).
 */
export default function LeadTypeCascadeSelect({
  value,
  onChange,
  disabled = false,
  required = false,
  tipoLabel,
  helperText,
}) {
  const { t } = useTranslation("settings");
  const { data: typesResp, isLoading } = useTypes(1, 1000, "");
  const rootTypes = useMemo(() => typesResp?.types || [], [typesResp?.types]);

  const current = value || emptyLeadTypeValue();

  const selectedRoot = useMemo(
    () => rootTypes.find((tp) => Number(tp.id) === Number(current.id_tipo)),
    [rootTypes, current.id_tipo]
  );
  const level1 = useMemo(() => selectedRoot?.children || [], [selectedRoot]);
  const selectedL1 = useMemo(
    () => level1.find((s) => Number(s.id) === Number(current.id_tipo1)),
    [level1, current.id_tipo1]
  );
  const level2 = useMemo(() => selectedL1?.children || [], [selectedL1]);
  const selectedL2 = useMemo(
    () => level2.find((s) => Number(s.id) === Number(current.id_tipo2)),
    [level2, current.id_tipo2]
  );
  const level3 = useMemo(() => selectedL2?.children || [], [selectedL2]);

  const emit = (patch) => onChange?.({ ...current, ...patch });

  const handleRoot = (val) => {
    emit({
      id_tipo: asNumOrNull(val),
      id_tipo1: null,
      id_tipo2: null,
      id_tipo3: null,
    });
  };
  const handleL1 = (val) => {
    emit({
      id_tipo1: asNumOrNull(val),
      id_tipo2: null,
      id_tipo3: null,
    });
  };
  const handleL2 = (val) => {
    emit({
      id_tipo2: asNumOrNull(val),
      id_tipo3: null,
    });
  };
  const handleL3 = (val) => {
    emit({ id_tipo3: asNumOrNull(val) });
  };

  const rootLabel = tipoLabel || t("agentsListPage.leadTypeCascadeTipo", { defaultValue: "Tipo" });

  return (
    <Stack spacing={1.5}>
      <TextField
        select
        fullWidth
        size="small"
        required={required}
        disabled={disabled || isLoading}
        label={rootLabel}
        value={current.id_tipo ?? ""}
        onChange={(e) => handleRoot(e.target.value)}
        helperText={helperText}
      >
        {!required && <MenuItem value="">—</MenuItem>}
        {isLoading && <MenuItem value="">…</MenuItem>}
        {!isLoading &&
          rootTypes.map((tp) => (
            <MenuItem key={tp.id} value={tp.id}>
              {titleOfType(tp)}
            </MenuItem>
          ))}
      </TextField>

      {level1.length > 0 && (
        <TextField
          select
          fullWidth
          size="small"
          disabled={disabled}
          label={t("agentsListPage.leadTypeCascadeSubtipo1", { defaultValue: "Subtipo 1" })}
          value={current.id_tipo1 ?? ""}
          onChange={(e) => handleL1(e.target.value)}
        >
          <MenuItem value="">—</MenuItem>
          {level1.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {titleOfType(s)}
            </MenuItem>
          ))}
        </TextField>
      )}

      {level2.length > 0 && (
        <TextField
          select
          fullWidth
          size="small"
          disabled={disabled}
          label={t("agentsListPage.leadTypeCascadeSubtipo2", { defaultValue: "Subtipo 2" })}
          value={current.id_tipo2 ?? ""}
          onChange={(e) => handleL2(e.target.value)}
        >
          <MenuItem value="">—</MenuItem>
          {level2.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {titleOfType(s)}
            </MenuItem>
          ))}
        </TextField>
      )}

      {level3.length > 0 && (
        <TextField
          select
          fullWidth
          size="small"
          disabled={disabled}
          label={t("agentsListPage.leadTypeCascadeSubtipo3", { defaultValue: "Subtipo 3" })}
          value={current.id_tipo3 ?? ""}
          onChange={(e) => handleL3(e.target.value)}
        >
          <MenuItem value="">—</MenuItem>
          {level3.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {titleOfType(s)}
            </MenuItem>
          ))}
        </TextField>
      )}
    </Stack>
  );
}
